import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Select, { components as selectComponents } from 'react-select';
import {
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Tooltip,
    Typography,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { FormService } from '../../../../modules/api/form';
import {
    buildApiFetchSelectBlockValue,
    parseApiFetchSelectDisplayValue,
} from '../../utils/apiFetchSelect';
import { getApiSelectControlStyles, useApiSelectStyles } from './apiSelect.style';

const DEFAULT_ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const API_SELECT_TOOLTIP_Z_INDEX = 100000;

/** Resolves display label text from a react-select option. */
const getOptionLabelText = (option, fallback = '') => {
    if (option == null) {
        return fallback == null ? '' : String(fallback);
    }

    const label = option.label ?? option.value ?? fallback;
    return label == null ? '' : String(label);
};

/** Builds tooltip text as "name (email)". */
const getOptionTooltipText = (option, fallback = '') => {
    if (option == null) {
        return fallback == null ? '' : String(fallback);
    }

    const name = getOptionLabelText(option, fallback);
    const email = option.email ?? option.raw?.email ?? '';
    const normalizedEmail = email == null ? '' : String(email).trim();

    if (!name) {
        return normalizedEmail;
    }

    if (!normalizedEmail) {
        return name;
    }

    return `${name} (${normalizedEmail})`;
};

/** Tooltip wrapper for truncated API select labels. */
const ApiSelectEllipsisLabel = ({ text, tooltipTitle, className }) => {
    const labelText = text == null ? '' : String(text);
    const tooltipText = tooltipTitle == null ? labelText : String(tooltipTitle);

    if (!labelText) {
        return <span className={className} />;
    }

    return (
        <Tooltip
            title={tooltipText || labelText}
            arrow
            placement="top"
            enterDelay={400}
            PopperProps={{ style: { zIndex: API_SELECT_TOOLTIP_Z_INDEX } }}
        >
            <span className={className}>{labelText}</span>
        </Tooltip>
    );
};

/** Returns the nearest ancestor with vertical scroll overflow. */
const findScrollableParent = (element) => {
    let current = element?.parentElement;
    while (current) {
        const { overflowY } = window.getComputedStyle(current);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }
    return null;
};

/** Stops wheel events from bubbling to modal/dialog scroll containers. */
const handleMenuWheel = (event) => {
    event.stopPropagation();
};

/** Keeps scroll inside the options list and blocks chain scroll to the modal. */
const handleOptionsWheel = (event) => {
    const viewport = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = viewport;
    const scrollingDown = event.deltaY > 0;
    const scrollingUp = event.deltaY < 0;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
        event.preventDefault();
    }
    event.stopPropagation();
};

const ApiSelectContext = createContext(null);

const getValueByPath = (obj, path) => {
    if (!path) return undefined;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

/** Maps a member API record to a react-select option using element config. */
const mapRecordToOption = (item, element) => {
    const labelField = element?.data?.labelField || 'name';
    const valueField = element?.data?.valueField || labelField;

    let label = item?.[labelField];
    if (!label && (item?.firstName || item?.lastName)) {
        label = [item.firstName, item.lastName].filter(Boolean).join(' ');
    }
    if (!label) {
        label = item?.email || item?._id || '';
    }

    const value = item?.[valueField] ?? label;
    return {
        value: String(value),
        label: String(label),
        email: item?.email == null ? '' : String(item.email),
        raw: item,
    };
};

/** Resolves total count from member list API response shapes. */
const resolveTotalCount = (response, itemsLength, pageNum, rowsPerPage) => {
    if (typeof response?.count === 'number') return response.count;
    if (typeof response?.total === 'number') return response.total;
    if (typeof response?.data?.total === 'number') return response.data.total;
    if (typeof response?.data?.count === 'number') return response.data.count;

    const loadedSoFar = (pageNum * rowsPerPage) + itemsLength;
    if (itemsLength >= rowsPerPage) {
        return loadedSoFar + 1;
    }
    return loadedSoFar;
};

const ApiSelectSearch = () => {
    const classes = useApiSelectStyles();
    const ctx = useContext(ApiSelectContext);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!ctx?.menuIsOpen) return undefined;

        const focusTimer = window.setTimeout(() => {
            inputRef.current?.focus();
            ctx.onKeepMenuOpen?.();
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [ctx?.menuIsOpen, ctx?.onKeepMenuOpen]);

    if (!ctx) return null;

    const {
        searchInputValue,
        searchPlaceholder,
        onSearchInputChange,
        onClearSearch,
    } = ctx;

    const stopMenuClose = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <div
            className={classes.searchWrap}
            onMouseDown={stopMenuClose}
            onTouchStart={stopMenuClose}
        >
            <SearchIcon className={classes.searchIconInline} />
            <input
                ref={inputRef}
                type="text"
                className={classes.searchInput}
                placeholder={searchPlaceholder || 'Search...'}
                value={searchInputValue}
                onChange={(event) => onSearchInputChange(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                onKeyUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />
            {searchInputValue ? (
                <IconButton
                    size="small"
                    className={classes.clearSearchBtn}
                    onMouseDown={stopMenuClose}
                    onTouchStart={stopMenuClose}
                    onClick={(event) => {
                        event.stopPropagation();
                        onClearSearch();
                        window.setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    aria-label="Clear search"
                >
                    <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
            ) : null}
        </div>
    );
};

const ApiSelectFooter = () => {
    const classes = useApiSelectStyles();
    const ctx = useContext(ApiSelectContext);

    if (!ctx) return null;

    const { isLoading, isSearching, loadedCount, totalCount, hasMore } = ctx;
    const showFooter = loadedCount > 0 || totalCount > 0 || isLoading || isSearching;

    if (!showFooter) return null;

    return (
        <div className={classes.menuFooter}>
            {isLoading && loadedCount > 0 ? <CircularProgress size={14} /> : null}
            <span>
                Showing {loadedCount}
                {totalCount > 0 ? ` of ${totalCount}` : ''}
                {hasMore ? ' · scroll for more' : ''}
            </span>
        </div>
    );
};

const ApiSelectPlaceholder = (placeholderProps) => {
    const classes = useApiSelectStyles();
    return (
        <Typography className={classes.placeholderText} {...placeholderProps.innerProps}>
            {placeholderProps.children}
        </Typography>
    );
};

const ApiSelectOption = (optionProps) => {
    const classes = useApiSelectStyles();
    const labelText = getOptionLabelText(optionProps.data, optionProps.children);
    const tooltipText = getOptionTooltipText(optionProps.data, optionProps.children);

    return (
        <MenuItem
            ref={optionProps.innerRef}
            selected={optionProps.isFocused}
            component="div"
            className={classes.option}
            {...optionProps.innerProps}
        >
            <ApiSelectEllipsisLabel
                text={labelText}
                tooltipTitle={tooltipText}
                className={classes.ellipsisText}
            />
        </MenuItem>
    );
};

const ApiSelectSingleValue = (singleValueProps) => {
    const classes = useApiSelectStyles();
    const labelText = getOptionLabelText(singleValueProps.data, singleValueProps.children);
    const tooltipText = getOptionTooltipText(singleValueProps.data, singleValueProps.children);

    return (
        <selectComponents.SingleValue {...singleValueProps}>
            <ApiSelectEllipsisLabel
                text={labelText}
                tooltipTitle={tooltipText}
                className={classes.ellipsisText}
            />
        </selectComponents.SingleValue>
    );
};

const ApiSelectNoOptionsMessage = (noOptionsProps) => {
    const classes = useApiSelectStyles();
    const ctx = useContext(ApiSelectContext);
    const { isSearching, searchQuery } = ctx || {};

    let message = 'Type to search';
    if (isSearching) {
        message = 'Searching...';
    } else if (searchQuery) {
        message = `No results for "${searchQuery}"`;
    }

    return (
        <Typography className={classes.noOptionsMessage} {...noOptionsProps.innerProps}>
            {message}
        </Typography>
    );
};

const ApiSelectInput = (inputProps) => (
    <selectComponents.Input {...inputProps} readOnly tabIndex={-1} />
);

const ApiSelectMenuList = (menuProps) => {
    const classes = useApiSelectStyles();
    return (
        <selectComponents.MenuList {...menuProps} className={classes.menuListInner}>
            {menuProps.children}
        </selectComponents.MenuList>
    );
};

const ApiSelectMenu = (menuProps) => {
    const classes = useApiSelectStyles();
    const ctx = useContext(ApiSelectContext);
    const loadMoreLockRef = useRef(false);

    const handleScroll = (event) => {
        if (!ctx?.onLoadMore || loadMoreLockRef.current) return;

        const target = event.target;
        const nearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 48;
        if (!nearBottom) return;

        loadMoreLockRef.current = true;
        ctx.onLoadMore();
        window.setTimeout(() => {
            loadMoreLockRef.current = false;
        }, 600);
    };

    const showInitialLoader = ctx && (ctx.isSearching || ctx.isLoading) && ctx.loadedCount === 0;
    const { onMouseDown: menuMouseDown, ...menuInnerProps } = menuProps.innerProps || {};

    return (
        <Paper
            square
            ref={ctx?.menuContainerRef}
            className={`${classes.menuPanel} api-select-menu-panel`}
            {...menuInnerProps}
            onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                menuMouseDown?.(event);
            }}
            onWheel={handleMenuWheel}
            onTouchMove={(event) => event.stopPropagation()}
        >
            <ApiSelectSearch />
            <div
                className={classes.optionsViewport}
                onScroll={handleScroll}
                onWheel={handleOptionsWheel}
                onTouchMove={(event) => event.stopPropagation()}
            >
                {showInitialLoader ? (
                    <div className={classes.loadingRow}>
                        <CircularProgress size={18} />
                    </div>
                ) : (
                    menuProps.children
                )}
            </div>
            <ApiSelectFooter />
        </Paper>
    );
};

const API_SELECT_COMPONENTS = {
    Menu: ApiSelectMenu,
    MenuList: ApiSelectMenuList,
    Input: ApiSelectInput,
    Option: ApiSelectOption,
    SingleValue: ApiSelectSingleValue,
    NoOptionsMessage: ApiSelectNoOptionsMessage,
    Placeholder: ApiSelectPlaceholder,
};

const CustomApiSelectField = (props) => {
    const {
        classes,
        element,
        dataObj,
        onChange,
        appId,
        menuPlacement,
        isInlineTableEdit,
        onSelectMenuOpen,
        onSelectMenuClose,
        openSelectMenuOnMount,
    } = props;
    const apiClasses = useApiSelectStyles();

    const rowsPerPage = element?.fetchAPI?.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE;
    const searchPlaceholder = element?.fetchAPI?.searchPlaceholder
        || 'Search by name or email...';
    const fieldKey = element.name || element.value;

    const [options, setOptions] = useState([]);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const searchDebounceRef = useRef(null);
    const fetchRequestIdRef = useRef(0);
    const hasFetchedRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const pageRef = useRef(0);
    const searchQueryRef = useRef('');
    const isLoadingRef = useRef(false);
    const hasMoreRef = useRef(false);
    const menuContainerRef = useRef(null);
    const controlWrapRef = useRef(null);
    const suppressCloseUntilRef = useRef(0);
    const lockedScrollParentRef = useRef(null);

    const isFocusInsideMenu = useCallback(() => {
        const activeElement = document.activeElement;
        if (!activeElement) return false;

        if (menuContainerRef.current?.contains(activeElement)) {
            return true;
        }

        return Boolean(activeElement.closest('.api-select-menu-panel'));
    }, []);

    /** Returns true when the event target is inside the control or portaled menu. */
    const isPointerInsideSelect = useCallback((target) => {
        if (!target) return false;
        if (controlWrapRef.current?.contains(target)) return true;
        if (menuContainerRef.current?.contains(target)) return true;
        return Boolean(target.closest?.('.api-select-menu-panel'));
    }, []);

    const resetMenuState = useCallback(() => {
        hasFetchedRef.current = false;
        fetchRequestIdRef.current += 1;
        loadingMoreRef.current = false;
        pageRef.current = 0;
        setIsSearching(false);
        setSearchInputValue('');
        setSearchQuery('');
        searchQueryRef.current = '';
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    }, []);

    const closeMenu = useCallback(() => {
        setMenuIsOpen(false);
        onSelectMenuClose?.();
        resetMenuState();
    }, [onSelectMenuClose, resetMenuState]);

    const keepMenuOpen = useCallback(() => {
        setMenuIsOpen(true);
    }, []);

    const rawValue = getValueByPath(dataObj, fieldKey);
    const value = useMemo(
        () => parseApiFetchSelectDisplayValue(rawValue, element),
        [rawValue, element],
    );

    const displayOptions = useMemo(() => {
        if (!value?.value) return options;
        if (options.some((opt) => opt.value === value.value)) return options;
        return [value, ...options];
    }, [options, value]);

    /** Fetches a page of members; ignores stale responses when a newer request is in flight. */
    const fetchPage = useCallback(async (pageNum, search, append = false) => {
        if (!appId) return;

        const requestId = fetchRequestIdRef.current + 1;
        fetchRequestIdRef.current = requestId;
        isLoadingRef.current = true;
        setIsLoading(true);
        if (!append) {
            setIsSearching(true);
        }

        try {
            const response = await FormService.getAllMembersList({
                appId,
                payload: {
                    page: pageNum,
                    rowsPerPage,
                    ...(search ? { search: String(search).trim() } : {}),
                    ...(element?.conditions?.length && { conditions: element.conditions }),
                },
            });

            if (requestId !== fetchRequestIdRef.current) return;

            const items = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [];
            const count = resolveTotalCount(response, items.length, pageNum, rowsPerPage);
            const mapped = items.map((item) => mapRecordToOption(item, element));

            let mergedResult = mapped;
            setOptions((prev) => {
                if (append) {
                    const seen = new Set(prev.map((opt) => opt.value));
                    mergedResult = [...prev];
                    mapped.forEach((opt) => {
                        if (!seen.has(opt.value)) {
                            mergedResult.push(opt);
                        }
                    });
                } else {
                    mergedResult = mapped;
                }
                return mergedResult;
            });

            const loadedCount = mergedResult.length;
            const nextHasMore = items.length >= rowsPerPage && loadedCount < count;
            setTotal(count);
            setHasMore(nextHasMore);
            hasMoreRef.current = nextHasMore;
            pageRef.current = pageNum;
        } catch (error) {
            if (requestId === fetchRequestIdRef.current && !append) {
                setOptions([]);
                setTotal(0);
                setHasMore(false);
                hasMoreRef.current = false;
            }
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                isLoadingRef.current = false;
                setIsLoading(false);
                setIsSearching(false);
                loadingMoreRef.current = false;
            }
        }
    }, [appId, element, rowsPerPage]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    /** Prevents the parent dialog/modal from scrolling while the dropdown menu is open. */
    useEffect(() => {
        if (!menuIsOpen) {
            return undefined;
        }

        const scrollParent = findScrollableParent(controlWrapRef.current);
        if (!scrollParent) {
            return undefined;
        }

        lockedScrollParentRef.current = scrollParent;
        const previousOverflow = scrollParent.style.overflow;
        const previousOverflowY = scrollParent.style.overflowY;
        scrollParent.style.overflow = 'hidden';
        scrollParent.style.overflowY = 'hidden';

        return () => {
            scrollParent.style.overflow = previousOverflow;
            scrollParent.style.overflowY = previousOverflowY;
            lockedScrollParentRef.current = null;
        };
    }, [menuIsOpen]);

    /** Closes the menu when the user clicks or taps outside the control and portaled menu. */
    useEffect(() => {
        if (!menuIsOpen) {
            return undefined;
        }

        const handleOutsidePointerDown = (event) => {
            if (isPointerInsideSelect(event.target)) {
                return;
            }
            closeMenu();
        };

        document.addEventListener('mousedown', handleOutsidePointerDown, true);
        document.addEventListener('touchstart', handleOutsidePointerDown, true);

        return () => {
            document.removeEventListener('mousedown', handleOutsidePointerDown, true);
            document.removeEventListener('touchstart', handleOutsidePointerDown, true);
        };
    }, [menuIsOpen, closeMenu, isPointerInsideSelect]);

    const runSearch = useCallback((query) => {
        const normalizedQuery = query == null ? '' : String(query).trim();
        searchQueryRef.current = normalizedQuery;
        setSearchQuery(normalizedQuery);
        pageRef.current = 0;
        loadingMoreRef.current = false;
        setOptions([]);
        setTotal(0);
        setHasMore(false);
        hasMoreRef.current = false;
        fetchPage(0, normalizedQuery, false);
    }, [fetchPage]);

    const handleSearchInputChange = useCallback((nextQuery) => {
        const normalizedQuery = nextQuery == null ? '' : String(nextQuery);
        setSearchInputValue(normalizedQuery);
        searchQueryRef.current = normalizedQuery;

        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = window.setTimeout(() => {
            runSearch(normalizedQuery);
        }, SEARCH_DEBOUNCE_MS);
    }, [runSearch]);

    const handleClearSearch = useCallback(() => {
        setSearchInputValue('');
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        runSearch('');
    }, [runSearch]);

    const handleMenuOpen = useCallback(() => {
        onSelectMenuOpen?.();
        setMenuIsOpen(true);
        suppressCloseUntilRef.current = Date.now() + 250;
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            pageRef.current = 0;
            fetchPage(0, searchQueryRef.current, false);
        }
    }, [fetchPage, onSelectMenuOpen]);

    const handleMenuClose = useCallback(() => {
        window.setTimeout(() => {
            if (Date.now() < suppressCloseUntilRef.current) {
                setMenuIsOpen(true);
                return;
            }
            if (isFocusInsideMenu()) {
                return;
            }
            closeMenu();
        }, 0);
    }, [closeMenu, isFocusInsideMenu]);

    const handleLoadMore = useCallback(() => {
        if (loadingMoreRef.current || isLoadingRef.current || !hasMoreRef.current) return;

        loadingMoreRef.current = true;
        const nextPage = pageRef.current + 1;
        fetchPage(nextPage, searchQueryRef.current, true);
    }, [fetchPage]);

    const contextValue = useMemo(() => ({
        menuIsOpen,
        menuContainerRef,
        onKeepMenuOpen: keepMenuOpen,
        searchInputValue,
        searchQuery,
        searchPlaceholder,
        onSearchInputChange: handleSearchInputChange,
        onClearSearch: handleClearSearch,
        isSearching,
        isLoading,
        loadedCount: options.length,
        totalCount: total,
        hasMore,
        onLoadMore: handleLoadMore,
    }), [
        menuIsOpen,
        keepMenuOpen,
        searchInputValue,
        searchQuery,
        searchPlaceholder,
        handleSearchInputChange,
        handleClearSearch,
        isSearching,
        isLoading,
        options.length,
        total,
        hasMore,
        handleLoadMore,
    ]);

    useEffect(() => {
        if (!openSelectMenuOnMount || element.readOnly) {
            return;
        }

        handleMenuOpen();
    }, [openSelectMenuOnMount, element.readOnly, handleMenuOpen]);

    return (
        <ApiSelectContext.Provider value={contextValue}>
            <div ref={controlWrapRef} className={apiClasses.controlWrap}>
                <Select
                id={fieldKey}
                className={`${apiClasses.root} ${classes?.selectField || ''}`}
                components={API_SELECT_COMPONENTS}
                isSearchable={false}
                menuIsOpen={menuIsOpen}
                value={value}
                placeholder={element?.placeholder || 'Select an option'}
                isClearable
                menuPlacement={menuPlacement || (isInlineTableEdit ? 'auto' : 'top')}
                isDisabled={element.readOnly}
                options={displayOptions}
                onChange={(selected) => {
                    const blockValue = buildApiFetchSelectBlockValue(selected, element);
                    onChange(fieldKey, blockValue, undefined, 'select', element, selected);
                    if (selected) {
                        closeMenu();
                    }
                }}
                isLoading={isLoading && options.length === 0}
                filterOption={() => true}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                closeMenuOnSelect
                blurInputOnSelect={false}
                tabSelectsValue={false}
                backspaceRemovesValue={false}
                openMenuOnFocus={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={getApiSelectControlStyles()}
                />
            </div>
        </ApiSelectContext.Provider>
    );
};

export default CustomApiSelectField;
